import { IconLanguage } from '@tabler/icons-react'

import { LanguageFlag } from '@/components/common/language-flag'
import { Button } from '@/components/sui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/sui/dropdown-menu'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useLanguageStore } from '@/stores/language'

import locales from './locales'

const SUPPORTED_LOCALES = [
  { value: 'deDe' as const, label: 'Deutsch', flag: 'de' as const },
  { value: 'enUs' as const, label: 'English', flag: 'us' as const },
  { value: 'esEs' as const, label: 'Español', flag: 'es' as const },
  { value: 'frFr' as const, label: 'Français', flag: 'fr' as const },
  { value: 'itIt' as const, label: 'Italiano', flag: 'it' as const },
  { value: 'ptBr' as const, label: 'Português', flag: 'br' as const },
]

interface LanguageSelectorProps {
  buttonClassName?: string
  iconClassName?: string
}

export function LanguageSelector({
  buttonClassName,
  iconClassName,
}: LanguageSelectorProps) {
  const { t } = useComponentTranslation(locales)
  const { locale, setLocale } = useLanguageStore()

  // The current locale's flag makes the trigger self-explanatory; the
  // generic globe icon only remains as a fallback for an unknown locale.
  const currentLocale = SUPPORTED_LOCALES.find((l) => l.value === locale)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant="ghost" size="icon" className={buttonClassName} />
        }
      >
        {currentLocale ? (
          <LanguageFlag
            code={currentLocale.flag}
            className={iconClassName ?? 'size-4'}
          />
        ) : (
          <IconLanguage className={iconClassName ?? 'size-4'} />
        )}
        <span className="sr-only">{t('changeLanguage')}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {SUPPORTED_LOCALES.map((l) => (
          <DropdownMenuItem
            key={l.value}
            onClick={() => setLocale(l.value)}
            className={locale === l.value ? 'bg-accent' : ''}
          >
            <LanguageFlag code={l.flag} /> {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
